import { useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";

export default function useReveal() {
  const { pathname } = useLocation();

  useLayoutEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      return undefined;
    }

    const hasIntersectionObserver = typeof IntersectionObserver !== "undefined";
    const viewportHeight = () => window.innerHeight || document.documentElement.clientHeight || 0;

    const isVisible = (element) => {
      const rect = element.getBoundingClientRect();
      const height = viewportHeight();

      if (height === 0) {
        return false;
      }

      return rect.bottom > 0 && rect.top < height * 0.88;
    };

    const revealFrames = new Set();

    const reveal = (element, observer) => {
      if (element.classList.contains("in")) {
        return;
      }

      // Let the hidden state paint once, then flip to the revealed state.
      const frameId = window.requestAnimationFrame(() => {
        revealFrames.delete(frameId);
        element.classList.add("in");

        if (observer) {
          observer.unobserve(element);
        }
      });

      revealFrames.add(frameId);
    };

    const observer = hasIntersectionObserver
      ? new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              reveal(entry.target, observer);
            }
          });
        }, { threshold: 0.12 })
      : null;

    const observeElement = (element) => {
      if (!(element instanceof Element) || !element.classList.contains("reveal")) {
        return;
      }

      if (element.classList.contains("in")) {
        return;
      }

      if (isVisible(element) || !observer) {
        reveal(element, observer);
        return;
      }

      observer.observe(element);
    };

    const scan = (root = document) => {
      root.querySelectorAll?.(".reveal").forEach(observeElement);
    };

    scan();

    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) {
            return;
          }

          observeElement(node);
          scan(node);
        });
      }
    });

    mutationObserver.observe(document.body, { childList: true, subtree: true });

    return () => {
      revealFrames.forEach((frameId) => window.cancelAnimationFrame(frameId));
      revealFrames.clear();
      observer?.disconnect();
      mutationObserver.disconnect();
    };
  }, [pathname]);
}
